/**
 * Position Validation Utilities
 *
 * Utility functions for validating and formatting geographic coordinates
 * for the Last Position feature.
 */

/**
 * Checks if coordinates represent the default/invalid position (0.0, 0.0)
 *
 * @param latitude - Latitude value
 * @param longitude - Longitude value
 * @returns true if position is (0.0, 0.0), false otherwise
 */
export function isDefaultPosition(
  latitude: number,
  longitude: number,
): boolean {
  return latitude === 0.0 && longitude === 0.0;
}

/**
 * Validates if coordinates are within valid geographic ranges
 *
 * @param latitude - Latitude value (must be between -90 and 90)
 * @param longitude - Longitude value (must be between -180 and 180)
 * @returns true if coordinates are valid, false otherwise
 */
export function isValidCoordinates(
  latitude: number,
  longitude: number,
): boolean {
  // Check if values are numbers
  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return false;
  }

  // Check if values are finite (not NaN, Infinity, or -Infinity)
  if (!isFinite(latitude) || !isFinite(longitude)) {
    return false;
  }

  // Check latitude range: -90 to 90
  if (latitude < -90 || latitude > 90) {
    return false;
  }

  // Check longitude range: -180 to 180
  if (longitude < -180 || longitude > 180) {
    return false;
  }

  return true;
}

/**
 * Formats a coordinate value to specified decimal precision
 *
 * @param value - Coordinate value to format
 * @param precision - Number of decimal places (default: 6)
 * @returns Formatted coordinate string
 */
export function formatCoordinates(
  value: number,
  precision: number = 6,
): string {
  // Validate input
  if (typeof value !== "number" || !isFinite(value)) {
    return "0.000000";
  }

  // Format to specified precision
  return value.toFixed(precision);
}

/**
 * Validates if position data is usable (valid coordinates and not default position)
 *
 * @param latitude - Latitude value
 * @param longitude - Longitude value
 * @returns true if position is usable, false otherwise
 */
export function isUsablePosition(latitude: number, longitude: number): boolean {
  return (
    isValidCoordinates(latitude, longitude) &&
    !isDefaultPosition(latitude, longitude)
  );
}
